// /pages/HR/components/charts/PieChart.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
    Chart as ChartJS,
    ArcElement,
    PieController,
    DoughnutController,
    Tooltip,
    Legend
} from 'chart.js';
import { Pie, Doughnut } from 'react-chartjs-2';
import './PieChart.css';

// Debug logging
console.log('[PieChart] Module loading...');

// Register Chart.js components
ChartJS.register(ArcElement, PieController, DoughnutController, Tooltip, Legend);

console.log('[PieChart] Registration complete. Controllers:',
  ChartJS.registry?.controllers?.items ? Object.keys(ChartJS.registry.controllers.items) : 'N/A');

/**
 * Reusable Pie/Doughnut Chart Component
 * Wrapper for Chart.js Pie chart with custom styling
 */
const PieChart = React.memo(({
    data = [],
    labels = [],
    title = '',
    type = 'pie', // 'pie' or 'doughnut'
    showLegend = true,
    legendPosition = 'right', // 'top', 'bottom', 'left', 'right'
    showPercentage = true,
    showValues = false,
    size = 300,
    loading = false,
    error = null,
    colors = null,
    onClick = null,
    onSegmentClick = null,
    animate = true,
    cutout = '0%', // For doughnut chart
    className = '',
    showTooltipPercentage = true,
    sortBy = null, // 'value-asc', 'value-desc', 'label-asc', 'label-desc'
}) => {
    const chartRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    // Memoized default colors
    const defaultColors = useMemo(() => [
        '#D01FD3', // Pink
        '#F46B02', // Orange
        '#8B5CF6', // Purple
        '#10B981', // Green
        '#3B82F6', // Blue
        '#EF4444', // Red
        '#F59E0B', // Yellow
        '#6B7280', // Gray
    ], []);

    // Generate gradient colors - memoized
    const generateGradientColors = useCallback((count) => {
        const colors = [];
        for (let i = 0; i < count; i++) {
            const hue = (i * 360) / count;
            colors.push(`hsl(${hue}, 70%, 50%)`);
        }
        return colors;
    }, []);

    // Process and sort data
    useEffect(() => {
        if (!data || data.length === 0) return;

        let processedData = [...data];
        let processedLabels = [...labels];

        // Sort data if requested
        if (sortBy) {
            const combined = processedLabels.map((label, index) => ({
                label,
                value: processedData[index]
            }));

            switch (sortBy) {
                case 'value-asc':
                    combined.sort((a, b) => a.value - b.value);
                    break;
                case 'value-desc':
                    combined.sort((a, b) => b.value - a.value);
                    break;
                case 'label-asc':
                    combined.sort((a, b) => a.label.localeCompare(b.label));
                    break;
                case 'label-desc':
                    combined.sort((a, b) => b.label.localeCompare(a.label));
                    break;
                default:
                    break;
            }

            processedLabels = combined.map(item => item.label);
            processedData = combined.map(item => item.value);
        }

        // Calculate percentages
        const total = processedData.reduce((sum, value) => sum + value, 0);
        const percentages = processedData.map(value =>
            total > 0 ? ((value / total) * 100).toFixed(1) : 0
        );

        // Prepare chart data
        const backgroundColors = colors ||
            (processedData.length > defaultColors.length ?
                generateGradientColors(processedData.length) :
                defaultColors.slice(0, processedData.length));

        // Create labels with percentages if needed
        const displayLabels = showPercentage ?
            processedLabels.map((label, index) =>
                `${label} (${percentages[index]}%)`
            ) : processedLabels;

        setChartData({
            labels: displayLabels,
            datasets: [{
                label: title || 'Distribution',
                data: processedData,
                backgroundColor: backgroundColors.map(color =>
                    color + (color.startsWith('#') ? 'CC' : '')
                ),
                borderColor: backgroundColors,
                borderWidth: 2,
                hoverOffset: 10,
                // Store original labels and percentages for tooltips
                originalLabels: processedLabels,
                percentages: percentages
            }]
        });
    }, [data, labels, colors, showPercentage, sortBy, title, defaultColors, generateGradientColors]);

    // Memoized handler for segment clicks
    const handleSegmentClick = useCallback((index) => {
        if (onSegmentClick && chartData) {
            const dataset = chartData.datasets[0];
            onSegmentClick({
                label: dataset.originalLabels[index],
                value: dataset.data[index],
                percentage: dataset.percentages[index],
                index
            });
        }
    }, [onSegmentClick, chartData]);

    // Memoized handler for retry
    const handleRetry = useCallback(() => {
        // TODO: API INTEGRATION POINT
        // Retry data fetching
        console.log('Retrying chart data fetch...');
    }, []);

    // Chart configuration - memoized
    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: animate ? {
            animateRotate: true,
            animateScale: false,
            duration: 1000,
            easing: 'easeOutQuart'
        } : false,
        cutout: type === 'doughnut' ? cutout : '0%',
        plugins: {
            legend: {
                display: showLegend,
                position: legendPosition,
                align: 'center',
                labels: {
                    font: {
                        family: 'Imprima, sans-serif',
                        size: 12
                    },
                    padding: 15,
                    generateLabels: (chart) => {
                        const dataset = chart.data.datasets[0];
                        return chart.data.labels.map((label, i) => ({
                            text: label,
                            fillStyle: dataset.backgroundColor[i],
                            strokeStyle: dataset.borderColor[i],
                            lineWidth: dataset.borderWidth,
                            hidden: false,
                            index: i,
                            value: dataset.data[i],
                            percentage: dataset.percentages ? dataset.percentages[i] : null
                        }));
                    },
                    usePointStyle: true,
                    pointStyle: 'rectRounded'
                },
                onClick: (e, legendItem) => {
                    if (onSegmentClick) {
                        handleSegmentClick(legendItem.index);
                    }
                }
            },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                padding: 12,
                cornerRadius: 8,
                titleFont: {
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 13
                },
                callbacks: {
                    label: function (context) {
                        const dataset = context.dataset;
                        const index = context.dataIndex;
                        const value = dataset.data[index];
                        const originalLabel = dataset.originalLabels[index];
                        const percentage = dataset.percentages[index];

                        let label = originalLabel + ': ';
                        label += new Intl.NumberFormat('en-US').format(value);

                        if (showTooltipPercentage) {
                            label += ` (${percentage}%)`;
                        }

                        return label;
                    },
                    afterLabel: function () {
                        return null;
                    }
                }
            },
            datalabels: showValues ? {
                display: true,
                color: '#fff',
                font: {
                    weight: 'bold',
                    size: 12
                },
                formatter: (value, context) => {
                    const dataset = context.dataset;
                    const percentage = dataset.percentages[context.dataIndex];
                    return showPercentage ? `${percentage}%` : value;
                }
            } : false
        },
        onHover: (event, activeElements) => {
            if (activeElements.length > 0) {
                setHoveredIndex(activeElements[0].index);
                if (chartRef.current?.canvas) {
                    chartRef.current.canvas.style.cursor = 'pointer';
                }
            } else {
                setHoveredIndex(null);
                if (chartRef.current?.canvas) {
                    chartRef.current.canvas.style.cursor = 'default';
                }
            }
        },
        onClick: (event, elements) => {
            if (elements.length > 0 && onSegmentClick) {
                handleSegmentClick(elements[0].index);
            } else if (onClick) {
                onClick(event);
            }
        }
    }), [
        animate, type, cutout, showLegend, legendPosition,
        showTooltipPercentage, showValues, showPercentage,
        onSegmentClick, onClick, handleSegmentClick
    ]);

    // Loading state
    if (loading) {
        return (
            <div className={`pie-chart-container loading ${className}`} style={{ height: size, width: size }}>
                <div className="chart-skeleton-circle">
                    <div className="skeleton-spinner"></div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={`pie-chart-container error ${className}`} style={{ height: size, width: size }}>
                <div className="chart-error">
                    <span className="error-icon">🥧</span>
                    <p className="error-message">{error}</p>
                    <button
                        className="retry-button"
                        onClick={handleRetry}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // No data state
    if (!chartData || chartData.datasets[0].data.length === 0) {
        return (
            <div className={`pie-chart-container no-data ${className}`} style={{ height: size, width: size }}>
                <div className="no-data-message">
                    <span className="no-data-icon">🥧</span>
                    <p>No data available</p>
                </div>
            </div>
        );
    }

    const ChartComponent = type === 'doughnut' ? Doughnut : Pie;

    return (
        <div className={`pie-chart-container ${className}`} style={{ maxHeight: size, maxWidth: size }}>
            {/* Center label for doughnut charts */}
            {type === 'doughnut' && hoveredIndex !== null && chartData && (
                <div className="doughnut-center-label">
                    <div className="center-value">
                        {chartData.datasets[0].data[hoveredIndex].toLocaleString()}
                    </div>
                    <div className="center-label">
                        {chartData.datasets[0].originalLabels[hoveredIndex]}
                    </div>
                </div>
            )}

            <ChartComponent
                ref={chartRef}
                data={chartData}
                options={options}
            />
        </div>
    );
});

PieChart.displayName = 'PieChart';

export default PieChart;
